import React, { useState } from "react";
import { Plus, Trash2, Clock, Save, Database } from "lucide-react";

export default function ACSchedule() {
  const [schedules, setSchedules] = useState([]);
  const [savedSchedules, setSavedSchedules] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const acUnits = ["AC Unit - 1", "AC Unit - 2", "AC Unit - 3", "AC Unit - 4"];

  const addSchedule = () => {
    const newSchedule = {
      id: Date.now(),
      day: "Monday",
      time: "12:00",
      action: "on",
      acUnit: acUnits[0],
      isActive: true,
    };
    setSchedules([...schedules, newSchedule]);
  };

  const removeSchedule = (id) => {
    setSchedules(schedules.filter((schedule) => schedule.id !== id));
  };

  const updateSchedule = (id, updates) => {
    setSchedules(
      schedules.map((schedule) =>
        schedule.id === id ? { ...schedule, ...updates } : schedule
      )
    );
  };

  const saveSchedules = () => {
    // In a real app, you would save to a database here
    setSavedSchedules(schedules);
    setShowSaved(true); // Automatically show saved schedules after saving
    alert(`Schedules saved successfully!\nTotal schedules: ${schedules.length}`);
  };

  const toggleShowSaved = () => {
    setShowSaved(!showSaved);
  };

  return (
    <div className="p-6 bg-gray-50/5 min-h-screen">
      <div className="bg-gray-50/5 shadow-md rounded-lg p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-50">AC Schedule Manager</h2>
          <div className="flex gap-2">
            <button
              onClick={addSchedule}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md shadow-md"
            >
              <Plus className="w-4 h-4" />
              Add Schedule
            </button>
            <button
              onClick={toggleShowSaved}
              className={`flex items-center gap-2 ${savedSchedules.length > 0 ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-400 cursor-not-allowed'} text-white px-4 py-2 rounded-md shadow-md`}
              disabled={savedSchedules.length === 0}
            >
              <Database className="w-4 h-4" />
              {showSaved ? "Hide Saved" : "Show Saved"}
            </button>
          </div>
        </div>

        {/* Current Schedules Form */}
        <div>
          {schedules.length === 0 && !showSaved ? (
            <div className="text-center py-12 text-white">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No schedules set. Click "Add Schedule" to create one.</p>
            </div>
          ) : showSaved ? (
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-black mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-400" />
                Saved Schedules
                <span className="text-sm text-white ml-auto">
                  {savedSchedules.length} schedules
                </span>
              </h3>
              {savedSchedules.map((schedule, index) => (
                <div
                  key={`saved-${index}`}
                  className="bg-purple-400/100 rounded-lg shadow-md p-4 grid grid-cols-1 md:grid-cols-5 gap-4 items-center border border-purple-100"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-700">Day</p>
                    <p className="font-semibold">{schedule.day}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Time</p>
                    <p className="font-semibold">{schedule.time}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">AC Unit</p>
                    <p className="font-semibold">{schedule.acUnit}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Action</p>
                    <p className="font-semibold capitalize">{schedule.action}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Status</p>
                    <p className={`font-semibold ${schedule.isActive ? 'text-green-600' : 'text-gray-600'}`}>
                      {schedule.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {schedules.map((schedule) => (
                <div
                  key={schedule.id}
                  className="bg-gray-100 rounded-lg shadow-md p-4 grid grid-cols-1 md:grid-cols-6 gap-4 items-center"
                >
                  {/* Day Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Day
                    </label>
                    <select
                      value={schedule.day}
                      onChange={(e) =>
                        updateSchedule(schedule.id, { day: e.target.value })
                      }
                      className="w-full text-gray-900 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {days.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Time Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time
                    </label>
                    <input
                      type="time"
                      value={schedule.time}
                      onChange={(e) =>
                        updateSchedule(schedule.id, { time: e.target.value })
                      }
                      className="w-full text-gray-900 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* AC Unit Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      AC Unit
                    </label>
                    <select
                      value={schedule.acUnit}
                      onChange={(e) =>
                        updateSchedule(schedule.id, { acUnit: e.target.value })
                      }
                      className="w-full text-gray-900 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {acUnits.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Action Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Action
                    </label>
                    <select
                      value={schedule.action}
                      onChange={(e) =>
                        updateSchedule(schedule.id, { action: e.target.value })
                      }
                      className="w-full text-gray-900                                                                                                                                                                                                                                          rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="on">Turn On</option>
                      <option value="off">Turn Off</option>
                    </select>
                  </div>

                  {/* Active Toggle */}
                  <div className="flex items-center justify-center">
                    <label className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">
                        Active
                      </span>
                      <input
                        type="checkbox"
                        checked={schedule.isActive}
                        onChange={(e) =>
                          updateSchedule(schedule.id, { isActive: e.target.checked })
                        }
                        className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </label>
                  </div>

                  {/* Delete Button */}
                  <div className="flex items-center justify-center">
                    <button
                      onClick={() => removeSchedule(schedule.id)}
                      className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-md shadow-md"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save Button */}
        {schedules.length > 0 && !showSaved && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={saveSchedules}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md shadow-md"
            >
              <Save className="w-4 h-4" />
              Save All Schedules
            </button>
          </div>
        )}
      </div>
    </div>
  );
}